import { Link } from 'react-router-dom';
import { Folder, Layers, Video, AudioLines, Image as ImageIcon, FileText, FolderOpen, Star } from 'lucide-react';
import { cn } from '@/lib/cn';
import { SelectionCheckbox } from './SelectionCheckbox';
import { ItemActionMenu } from './ItemActionMenu';
import { useItemListContext } from './ItemListProvider';
import { useItemActions } from '@/hooks/useItemActions';
import type { NormalizedItem } from './types';

interface ItemCardProps {
  item: NormalizedItem;
}

export function ItemCard({ item }: ItemCardProps) {
  const { isSelected, updateIsQuerying, onRefresh } = useItemListContext();
  const { isFavorite } = useItemActions({ updateIsQuerying, onRefresh });
  const selected = isSelected(item.id);
  const favorite = isFavorite(item.id);
  const isProject = item.itemtype === 'project';
  const Icon = isProject ? Layers : Folder;

  const linkTo = `/in/${item.itemtype}/${item.id}`;
  const previewImages = (item.previewImages as Array<{ S?: string; url?: string }>) || [];
  const hasPreviewImages = previewImages.length > 0;

  const resourceCounts = getResourceCounts(item.resources);
  const isEmpty = resourceCounts.length === 0 || resourceCounts.every((r) => r.count === 0);

  return (
    <Link to={linkTo} className="block no-underline">
      <div
        className={cn(
          'group relative flex flex-col rounded-xl border transition-all',
          selected
            ? 'border-primary bg-primary-muted shadow-md'
            : 'border-border/60 bg-card shadow-sm hover:shadow-md hover:border-border',
        )}
      >
        {/* Preview area */}
        <div
          className={cn(
            'flex aspect-[4/3] items-center justify-center overflow-hidden rounded-t-[11px]',
            hasPreviewImages ? 'bg-secondary' : isProject ? 'bg-primary-muted' : 'bg-info-light',
          )}
        >
          {hasPreviewImages ? (
            <PreviewGrid images={previewImages} />
          ) : (
            <Icon
              className={cn(
                'h-14 w-14',
                isProject ? 'text-primary' : 'text-info',
              )}
            />
          )}
        </div>

        {/* Info row */}
        <div className="flex items-center gap-2 px-3 py-2">
          <Icon
            className={cn(
              'h-4 w-4 shrink-0',
              isProject ? 'text-primary' : 'text-info',
            )}
          />
          <p className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
            {item.title}
          </p>
          {favorite && <Star className="h-3.5 w-3.5 shrink-0 fill-yellow-400 text-yellow-400" />}
          <ItemActionMenu item={item} />
        </div>

        {/* Footer: resource counts or empty state — always rendered for consistent card height */}
        <div className="flex items-center gap-2 border-t border-border-light px-3 py-1.5 min-h-[30px]">
          {isEmpty ? (
            <span className="text-xs text-muted-foreground">
              {isProject ? 'Empty project' : 'Empty folder'}
            </span>
          ) : (
            resourceCounts.map(({ key, count, icon: CountIcon }) => (
              <span
                key={key}
                className="flex items-center gap-0.5 text-xs text-muted-foreground"
                title={`${count} ${key}`}
              >
                <span>{count}</span>
                <CountIcon className="h-3 w-3" />
              </span>
            ))
          )}
        </div>

        {/* Selection checkbox — always visible for discoverability */}
        <div className="absolute left-2 top-2">
          <SelectionCheckbox item={item} />
        </div>
      </div>
    </Link>
  );
}

function PreviewGrid({ images }: { images: Array<{ S?: string; url?: string }> }) {
  const srcs = images.slice(0, 4).map((img) => img.S || img.url || '');
  return (
    <div className="grid h-full w-full grid-cols-2 gap-0.5">
      {srcs.map((src, i) => (
        <img
          key={i}
          src={src}
          alt=""
          className={cn(
            'h-full w-full object-cover',
            srcs.length === 1 && 'col-span-2 row-span-2',
            srcs.length === 3 && i === 0 && 'row-span-2',
          )}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      ))}
    </div>
  );
}

const RESOURCE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  video: Video,
  audio: AudioLines,
  image: ImageIcon,
  doc: FileText,
  folder: FolderOpen,
  project: Layers,
};

function getResourceCounts(
  resources: unknown,
): Array<{ key: string; count: number; icon: React.ComponentType<{ className?: string }> }> {
  if (!resources || typeof resources !== 'object' || Array.isArray(resources)) return [];
  const counts = resources as Record<string, number | { N?: string }>;
  const result: Array<{ key: string; count: number; icon: React.ComponentType<{ className?: string }> }> = [];
  for (const [key, val] of Object.entries(counts)) {
    const icon = RESOURCE_ICONS[key];
    if (!icon) continue;
    const num = typeof val === 'number' ? val : Number(val?.N || 0);
    if (num > 0) {
      result.push({ key, count: num, icon });
    }
  }
  return result;
}
